import React, { FC, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hideEcHeaderNavi } from '@app/react/common/Function';
import { OrderMozikokuinComponent } from '@component/index';
import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';
import { IinitState } from '@app/react/reducer';
import {
    apiGetActions,
    partsSelectActions,
} from '@app/react/action';
import { showOrderPartsHelpAction } from '@app/react/action/showOrderPartsHelp';
import { useHistoryGoParts } from '@app/react/contexts/useHistoryGoParts';
import type { PartsProduct } from '@app/react/const/type';
import type { AppState } from '@app/react/reducer';
import { validateCarving } from '@app/react/common/validateCarving';
import {
    noProduct,
    yesProduct as originYesProduct,
} from '@app/react/const/products/carving';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';
import { selectOrderPartsHelp } from './selectors';

const checkCarvingFin = (
    status: boolean,
    selectedProduct: PartsProduct | null,
    carvingString: string | null,
) => {
    if (
        status === true
        && (selectedProduct === null
         || carvingString === null
         || carvingString?.length === 0)
    ) {
        return false;
    }

    return true;
};

export const OrderMozikokuinContainer: FC<{ waitFetch?: boolean }> = ({ waitFetch = false }) => {
    const dispatch = useDispatch();
    const { arrowType, orderType } = useOrderPathParams();
    const isDesktop = useMediaDesktop();
    const isMobile = useMediaMobile();

    const customizeData = useSelector<AppState, IinitState>(
        (state) => state.customize,
    );
    const status = useSelector<AppState, boolean>(
        (state) => state.customize.carvingOk,
    );
    const selectedProduct = useSelector<AppState, PartsProduct | null>(
        (state) => state.customize.carving,
    );
    const carvingString = useSelector<AppState, string | null>(
        (state) => state.customize.carvingString,
    );
    const partsData = useSelector<AppState, Array<PartsProduct>>(
        (state) => state.APIData.carving?.partsData || [],
    );

    const openHelpParts = useSelector(selectOrderPartsHelp);
    const isOpenHelp = openHelpParts === 'carving';

    const yesProduct = useMemo(() => {
        const prices = partsData.map((item) => item.price);
        if (prices === undefined || prices.length === 0) {
            return originYesProduct;
        }
        const min = Math.min(...prices);
        return {
            ...originYesProduct,
            price: min,
        };
    }, [partsData]);

    const goParts = useHistoryGoParts();
    const onOk = () => {
        const ret = checkCarvingFin(status, selectedProduct, carvingString);
        if (!ret) {
            alert('刻印文字を入力してください');
            return;
        }
        goParts();
    };
    const handleCustomizeClick = () => {
        const ret = checkCarvingFin(status, selectedProduct, carvingString);
        if (!ret) {
            alert('刻印文字を入力してください');
            return;
        }
        goParts('preview');
    };

    const onChangeStatus = (value: boolean) => {
        const payload = (() => {
            if (value) {
                return {
                    carvingOk: true,
                    carving: selectedProduct,
                    carvingString,
                };
            }
            return {
                carvingOk: false,
                carving: null,
                carvingString: null,
            };
        })();
        dispatch(partsSelectActions.carving.started(payload));
    };

    const onSelectProduct = (product: PartsProduct) => {
        dispatch(
            partsSelectActions.carving.started({
                carvingOk: true,
                carving: product,
                carvingString,
            }),
        );
    };

    const onChangeText: React.ChangeEventHandler<HTMLInputElement> = (
        event,
    ) => {
        const mode = selectedProduct?.options.carvingWritingType || -1;
        const [valid, msg] = validateCarving(event.target.value, mode);
        if (!valid) {
            alert(msg);
            return;
        }

        dispatch(
            partsSelectActions.carving.started({
                carvingOk: true,
                carving: selectedProduct,
                carvingString: event.target.value,
            }),
        );
    };

    const openHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: 'carving' }));
    }, [dispatch]);

    const closeHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: null }));
    }, [dispatch]);

    useEffect(() => {
        hideEcHeaderNavi(isDesktop, isMobile, dispatch);
    }, [isDesktop, isMobile, dispatch]);

    useEffect(() => {
        if (waitFetch) {
            console.log('initializing, waiting fetch');
            return;
        }
        dispatch(
            apiGetActions.carving.started({
                arrowType,
                orderType,
                selectItems: customizeData,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, arrowType, orderType, waitFetch]);

    return (
        <OrderMozikokuinComponent
            onOk={onOk}
            onCustomizeClick={handleCustomizeClick}
            status={status}
            onChangeStatus={onChangeStatus}
            viewData={partsData}
            selectedProduct={selectedProduct}
            onSelectProduct={onSelectProduct}
            carvingString={carvingString}
            onChangeText={onChangeText}
            noProduct={noProduct}
            yesProduct={yesProduct}
            isHelpOpen={isOpenHelp}
            handleHelpOpen={openHelp}
            handleHelpClose={closeHelp}
        />
    );
};
